/**
 * A tela do Funil (histórias 3.2 e 3.8).
 *
 *     /funil?funil=<uuid>    o quadro por Etapa e o formulário de Negócio
 *
 * Ela é filha de `rotaDaAplicacao`, e por isso herda a guarda de sessão do layout — registrada
 * uma vez lá, ela vale para toda rota interna.
 *
 * **O caminho continua vindo de `CAMINHOS`.** O que mudou na 3.2 foi só quem desenha o corpo:
 * `/funil` saiu do laço de placeholders de `telas.ts` (via `COM_TELA_PROPRIA`) e ganhou arquivo
 * próprio, como `/administracao` na 2.3 e `/empresas` na 2.6. Item de menu e rota continuam
 * nascendo da **mesma** lista.
 *
 * **O quadro por Etapa chegou na 3.8, e ele é a tela.** Até a 3.7 esta rota era só a porta de
 * entrada — um formulário e uma nota dizendo que o quadro viria —, porque não havia rota de
 * **leitura** de Negócio para alimentar coluna nenhuma. Ela nasceu na 3.8
 * (`GET /negocios?funil_id=`), e com ela o quadro passou a ser o corpo da tela; o formulário
 * ficou como o cartão de baixo, que é onde a porta de entrada pertence quando a carteira já
 * está visível.
 *
 * **Os dois cartões dividem o mesmo `?funil=`, e isso não é engano.** A pílula do quadro é
 * **navegação** ("que quadro estou olhando"); o seletor de Funil dentro do formulário é **campo**
 * ("em que Funil este Negócio nasce"). Os dois escrevem o mesmo parâmetro, então nunca discordam,
 * e a URL continua sendo a única dona do "onde eu estou".
 *
 * **O Funil escolhido mora na URL, e não num `useState`.** Ele é o "onde eu estou" de quem
 * trabalha: um link do quadro, um "voltar" do navegador, uma aba recarregada. Em estado local ele
 * se perderia nas três, e sobretudo ao atravessar os 768px do shell (girar o aparelho,
 * redimensionar a janela), que troca a árvore e remonta o `<Outlet/>`.
 *
 * **O padrão por perfil é do SDR para Prospecção, e de Vendas para os demais (UX-DR4).** A regra
 * é da decisão de UX do **Funil** — "SDR abre direto na Prospecção" está escrita na UX-DR4
 * (`docs/planning-artifacts/epics.md`), e não na UX-DR3, que descreve a tela "Hoje" (3.9). Este
 * arquivo citou a vizinha até a revisão da 3.8. O SDR
 * trabalha a prospecção, e abrir a tela dele no funil de Vendas custaria um clique em toda
 * abertura. Ele é **padrão**, e não recorte: qualquer perfil troca de Funil no seletor, porque
 * quem recorta o que cada um enxerga é a RLS do serviço, linha a linha — e não a tela. Desde a
 * 3.7 o casamento é pelo campo `tipo`, e não pelo nome do Funil: nome é dado, e a gestão o
 * renomeia quando quiser.
 *
 * **Sem cabeçalho próprio.** O `Cabecalho` do shell já desenha o título ("Funil") e o subtítulo
 * do item de navegação; os cartões desta tela têm nome distinto ("Quadro por etapa" e "Novo
 * negócio"), como a "Base comercial" da 2.6 sob o título "Empresas".
 *
 * **A chegada pelo WhatsApp (13.1).** O "+ Negócio para esta pessoa" navega para
 * `/funil?novo=1&empresa=<id>` (conversa com Empresa) ou `?novo=1&pessoa=<id>` (Pessoa solta). Na
 * URL vão **só ids**; o nome do contato vai no *state* do histórico, e nunca nome ou telefone na
 * barra de endereço. A tela lê os ids **uma vez**, ao montar, guarda-os em estado e **limpa a URL**
 * (`replace`): recarregar não reabre a janela, fechar e abrir de novo pelo "+ Negócio" abre limpa, e
 * trocar de Funil não perde a Empresa nem a Pessoa (estado, e não URL).
 */

import { Link, createRoute, useRouterState } from '@tanstack/react-router'
import {
  type KeyboardEvent as TeclaNaJanela,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Dialogo } from '@/modulos/negocios/Dialogo'
import {
  type EstadoDoVinculoNaJanela,
  FormularioDeNegocio,
} from '@/modulos/negocios/FormularioDeNegocio'
import { QuadroDoFunil, ROTULO_DO_NOVO_NEGOCIO } from '@/modulos/negocios/QuadroDoFunil'
import { type Funil, useFunis } from '@/modulos/negocios/api'
import { useAbrirNegocio } from '@/modulos/negocios/navegacao-do-negocio'
import { useSessao } from '@/modulos/identidade/sessao'
import '@/modulos/negocios/funis-e-etapas.css'
import { AcoesDoCabecalho } from '@/shell/Shell'
import {
  CAMINHOS,
  CAMINHO_DE_GERENCIAR_FUNIS,
  TITULO_DE_GERENCIAR_FUNIS,
  ehDeGestao,
} from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'

/*
 * O *state* do histórico que o "+ Negócio para esta pessoa" leva (13.1): o nome **exibido** do
 * contato — o que a conversa mostra —, que nunca vai na URL.
 */
declare module '@tanstack/history' {
  interface HistoryState {
    nomeDoContato?: string
  }
}

/** Um `uuid` no formato canônico — o resto que chega em `empresa`/`pessoa` é descartado. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** O que a busca do cabeçalho procura — o texto é o do protótipo v2. */
export const ROTULO_DA_BUSCA = 'Buscar empresa ou negócio'

/** O que a URL desta tela carrega. O campo é opcional: `/funil` cru é válido. */
export interface BuscaDoFunil {
  /** O `uuid` do Funil aberto. Ausente é "o padrão do meu perfil". */
  funil?: string
  /**
   * Abrir a tela já com a janela "Novo negócio" (quarta passada, 29/09/2026): é o destino do
   * "+ Negócio para esta pessoa" da conversa de WhatsApp, que não tem como abrir a janela de lá.
   */
  novo?: boolean
  /** A Empresa da conversa de WhatsApp (13.1): a janela abre com ela escolhida. */
  empresa?: string
  /** A Pessoa da conversa (13.1): relida; se solta, vinculada à Empresa do Negócio criado. */
  pessoa?: string
}

/** O que a janela do Novo negócio recebe da chegada — os ids da conversa, lidos uma vez. */
interface ChegadaDaConversa {
  empresaId?: string
  pessoaId?: string
  nomeDoContato?: string
}

/**
 * Os dois tipos de Funil, e é por **tipo** que o padrão por perfil os encontra.
 *
 * Até a 3.7 o casamento era por **nome** (`'Prospecção'`), e a docstring daqui dizia com todas
 * as letras que aquilo era frouxo de propósito: não havia outra coisa a usar. Os `id` são
 * gerados na migração de cada instalação, e a coluna que distingue Prospecção de Vendas não
 * existia.
 *
 * Agora existe: `tipo` sai em `GET /funis` e é o mesmo discriminador que a RLS do serviço lê
 * para decidir quem enxerga cada Negócio (FR-31). Isso muda o que a regra promete — o nome é
 * **dado**, e a gestão o renomeia quando quiser (FR-1, FR-63); o tipo não entra em corpo nenhum.
 * Renomear os dois Funis deixou de derrubar o padrão por perfil.
 *
 * O `?? funis[0]` fica: uma instalação cujos Funis sejam todos do outro tipo cai no primeiro da
 * lista, que é um padrão honesto, e não num seletor sem valor.
 */
const TIPO_DE_PROSPECCAO: Funil['tipo'] = 'prospeccao'
const TIPO_DE_VENDAS: Funil['tipo'] = 'vendas'

/**
 * O Funil que abre para aquele perfil, ou o primeiro da lista quando o tipo não estiver lá.
 *
 * Função pura e exportada porque é ela que a suíte observa: montar a tela inteira para afirmar
 * qual `option` está marcada custaria caro para uma regra de duas linhas.
 *
 * **É padrão, e não recorte.** Qualquer perfil troca de Funil no seletor — quem recorta o que
 * cada um enxerga é a RLS do serviço, linha a linha, e não a tela.
 */
export function funilPadrao(
  funis: readonly Funil[],
  perfil: string | undefined,
): Funil | undefined {
  const procurado = perfil === 'sdr' ? TIPO_DE_PROSPECCAO : TIPO_DE_VENDAS
  return funis.find((funil) => funil.tipo === procurado) ?? funis[0]
}

/** Os elementos que recebem foco por `Tab` dentro da janela — o que a armadilha de foco percorre. */
const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** O que a janela pergunta antes de descartar um formulário já preenchido. */
export const PERGUNTA_DO_NOVO_NEGOCIO = 'O que foi digitado no negócio novo se perde.'

/** 13.14b (DW-383): a pergunta antes de fechar com o contato da conversa solto. */
export const TITULO_DO_CONTATO_SOLTO = 'O contato ainda está solto'
export function perguntaDoContatoSolto(codigo: number | undefined): string {
  const negocio = codigo === undefined ? 'O Negócio' : `O Negócio ${String(codigo)}`
  return (
    `${negocio} foi criado, mas o contato do WhatsApp não está ligado à Empresa dele. ` +
    'Você pode ligá-lo depois pelo painel do contato no WhatsApp.'
  )
}

/**
 * Há algo digitado no formulário? Os seletores de Funil e Etapa nascem preenchidos e não contam, e
 * o campo Empresa preenchido **pela conversa** (`data-nao-conta`, 13.1) também não: ninguém digitou
 * nada, e fechar sem perguntar é o que se espera.
 */
function temAlgoDigitado(caixa: HTMLElement | null): boolean {
  if (caixa === null) {
    return false
  }
  return Array.from(
    caixa.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([data-nao-conta]), textarea',
    ),
  ).some((campo) => campo.value.trim() !== '')
}

/**
 * A criação de Negócio (3.2) em modal, aberta pelo `+ Negócio` do Funil (11.4) — o formulário
 * deixou de ser um cartão fixo embaixo do quadro.
 *
 * - O foco entra na caixa ao abrir, **fica nela** (`Tab` e `Shift+Tab` dão a volta) e **volta** a
 *   quem a abriu ao fechar.
 * - `Esc` e o fundo **perguntam** antes de descartar um formulário preenchido (modal estreito);
 *   "Fechar" é ato explícito e sai direto.
 * - O fundo só fecha quando o gesto **começou e terminou** nele: arrastar uma seleção de texto de
 *   dentro do campo até o fundo não descarta nada.
 * - **13.14b (DW-372, DW-383):** do envio ao fim do vínculo do contato da conversa, "Fechar", `Esc`
 *   e o fundo **não fecham** (a falha do vínculo morreria com a janela); com o contato solto (a
 *   falha), os três perguntam antes — "Fechar mesmo assim" fecha, "Voltar e tentar de novo" (e o
 *   `Esc` da pergunta) volta com o foco no "Tentar de novo". A pergunta do contato vence a do
 *   "Descartar?". A navegação do navegador não é barrada (limitação declarada).
 */
function JanelaDoNovoNegocio({
  funilId,
  aoTrocarDeFunil,
  aoFechar,
  chegada,
}: {
  funilId: string | undefined
  aoTrocarDeFunil: (funilId: string) => void
  aoFechar: () => void
  chegada: ChegadaDaConversa
}) {
  const caixa = useRef<HTMLDivElement>(null)
  const comecouNoFundo = useRef(false)
  const abrirNegocio = useAbrirNegocio()
  /* O Negócio que o "Abrir o negócio" pede, aberto quando a janela de fato fecha. */
  const negocioAAbrir = useRef<string | null>(null)
  const concluirOFechar = useCallback(() => {
    const negocioId = negocioAAbrir.current
    negocioAAbrir.current = null
    aoFechar()
    if (negocioId !== null) {
      abrirNegocio(negocioId)
    }
  }, [aoFechar, abrirNegocio])
  const [confirmando, setConfirmando] = useState(false)
  /* 13.14b: o vínculo do contato da conversa, como o formulário o conta. */
  const [vinculo, setVinculo] = useState<{ estado: EstadoDoVinculoNaJanela; codigo?: number }>({
    estado: 'livre',
  })
  const [perguntandoDoContato, setPerguntandoDoContato] = useState(false)
  const aoMudarOVinculo = useCallback((estado: EstadoDoVinculoNaJanela, codigo?: number) => {
    setVinculo({ estado, codigo })
  }, [])

  const pedirParaFechar = useCallback(() => {
    if (vinculo.estado === 'no-ar') {
      negocioAAbrir.current = null
      return
    }
    if (vinculo.estado === 'pendente') {
      setPerguntandoDoContato(true)
      return
    }
    if (temAlgoDigitado(caixa.current)) {
      setConfirmando(true)
      return
    }
    concluirOFechar()
  }, [concluirOFechar, vinculo.estado])

  /* "Fechar" é ato explícito e não pergunta o "Descartar?" — mas o contato solto, sim. */
  const fecharPeloBotao = () => {
    negocioAAbrir.current = null
    if (vinculo.estado === 'pendente') {
      setPerguntandoDoContato(true)
      return
    }
    aoFechar()
  }

  const voltarParaTentar = () => {
    negocioAAbrir.current = null
    setPerguntandoDoContato(false)
    const tentar = caixa.current?.querySelector<HTMLElement>('[data-tentar-o-vinculo]')
    ;(tentar ?? caixa.current)?.focus()
  }

  useEffect(() => {
    const quemAbriu = document.activeElement instanceof HTMLElement ? document.activeElement : null
    caixa.current?.focus()
    return () => {
      quemAbriu?.focus()
    }
  }, [])

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (
        evento.key === 'Escape' &&
        !evento.defaultPrevented &&
        !confirmando &&
        !perguntandoDoContato
      ) {
        pedirParaFechar()
      }
    }
    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [pedirParaFechar, confirmando, perguntandoDoContato])

  const prenderOFoco = (evento: TeclaNaJanela) => {
    if (evento.key !== 'Tab' || caixa.current === null) {
      return
    }
    const focaveis = Array.from(caixa.current.querySelectorAll<HTMLElement>(FOCAVEIS))
    const primeiro = focaveis[0]
    const ultimo = focaveis[focaveis.length - 1]
    if (primeiro === undefined || ultimo === undefined) {
      evento.preventDefault()
      return
    }
    const ativo = document.activeElement
    if (evento.shiftKey && (ativo === primeiro || ativo === caixa.current)) {
      evento.preventDefault()
      ultimo.focus()
    } else if (!evento.shiftKey && ativo === ultimo) {
      evento.preventDefault()
      primeiro.focus()
    }
  }

  return (
    <div
      className="tela-do-funil__fundo"
      role="presentation"
      onMouseDown={(evento) => {
        comecouNoFundo.current = evento.target === evento.currentTarget
      }}
      onClick={(evento) => {
        const doFundo = comecouNoFundo.current && evento.target === evento.currentTarget
        comecouNoFundo.current = false
        if (doFundo) {
          pedirParaFechar()
        }
      }}
    >
      <div
        ref={caixa}
        className="tela-do-funil__janela"
        role="dialog"
        aria-modal="true"
        aria-label="Novo negócio"
        tabIndex={-1}
        onKeyDown={prenderOFoco}
      >
        <div className="tela-do-funil__janela-topo">
          <button
            type="button"
            className="tela-do-funil__fechar"
            disabled={vinculo.estado === 'no-ar'}
            onClick={fecharPeloBotao}
          >
            Fechar
          </button>
        </div>
        <FormularioDeNegocio
          funilId={funilId}
          aoTrocarDeFunil={aoTrocarDeFunil}
          empresaInicialId={chegada.empresaId}
          pessoaSoltaId={chegada.pessoaId}
          nomeDoContato={chegada.nomeDoContato}
          aoMudarOVinculo={aoMudarOVinculo}
          aoAbrirONegocio={(negocioId) => {
            //: 13.20b2: a Pessoa já tem Negócio em andamento — a janela fecha pela guarda de sempre
            //: (code review, B10: com algo digitado, pergunta antes) e o modal dele abre, numa
            //: navegação só.
            negocioAAbrir.current = negocioId
            pedirParaFechar()
          }}
        />
      </div>
      {confirmando ? (
        <Dialogo
          titulo="Descartar o negócio novo?"
          subtitulo={PERGUNTA_DO_NOVO_NEGOCIO}
          rotuloDoConfirmar="Descartar e fechar"
          ocupado={false}
          erro={undefined}
          aoFechar={() => {
            negocioAAbrir.current = null
            setConfirmando(false)
            caixa.current?.focus()
          }}
          aoConfirmar={concluirOFechar}
        />
      ) : null}
      {perguntandoDoContato ? (
        <Dialogo
          titulo={TITULO_DO_CONTATO_SOLTO}
          subtitulo={perguntaDoContatoSolto(vinculo.codigo)}
          rotuloDoConfirmar="Fechar mesmo assim"
          ocupado={false}
          erro={undefined}
          aoFechar={voltarParaTentar}
          aoConfirmar={concluirOFechar}
          acaoSecundaria={{ rotulo: 'Voltar e tentar de novo', aoAcionar: voltarParaTentar }}
        />
      ) : null}
    </div>
  )
}

/**
 * `TelaDoFunil`, e não `Funil`: o tipo `Funil` do módulo já mora neste arquivo.
 */
function TelaDoFunil() {
  // A anotação é explícita porque `useSearch` da própria rota que a declara vê o tipo em
  // construção; `validateSearch` continua sendo o dono do formato.
  const { funil, novo, empresa, pessoa }: BuscaDoFunil = rotaFunil.useSearch()
  const navegar = rotaFunil.useNavigate()
  const nomeDoContato = useRouterState({
    select: (estado) => estado.location.state.nomeDoContato,
  })
  const { data: sessao } = useSessao()
  const catalogo = useFunis()
  /*
   * `?novo=1` abre a janela já na chegada — e só na chegada. Os ids da conversa são lidos **aqui,
   * uma vez**, e vivem em estado: a URL é limpa logo abaixo, e é o estado que a troca de Funil
   * preserva. `null` é a janela fechada; `{}` é a janela aberta pelo "+ Negócio", limpa.
   */
  const [criacao, setCriacao] = useState<ChegadaDaConversa | null>(() =>
    novo === true
      ? {
          empresaId: empresa,
          pessoaId: pessoa,
          nomeDoContato,
        }
      : null,
  )
  /*
   * A chegada **com a tela já montada** (13.1): outro link para `/funil?novo=1&empresa=…` não
   * remonta a rota, e o `useState` acima já foi lido. A chave da chegada é comparada na própria
   * renderização (o ajuste de estado por mudança de entrada, sem efeito): mudou e tem `novo`, a
   * janela abre com os ids **antes** de o efeito abaixo limpar a URL. Limpa a URL, a chave volta a
   * `null`, e a próxima chegada igual abre de novo.
   */
  const [geracao, setGeracao] = useState(0)
  const chegada = novo === true ? `${empresa ?? ''}|${pessoa ?? ''}` : null
  const [chegadaLida, setChegadaLida] = useState<string | null>(chegada)
  if (chegada !== chegadaLida) {
    setChegadaLida(chegada)
    if (chegada !== null) {
      setCriacao({ empresaId: empresa, pessoaId: pessoa, nomeDoContato })
      // Uma janela nova, e não a de antes com outros ids: o rascunho e a escolha dela eram de
      // outra chegada.
      setGeracao((atual) => atual + 1)
    }
  }
  /*
   * A limpeza da URL (`replace`, sem entrada nova de histórico): sem ela, recarregar reabriria a
   * janela e reaplicaria os ids da conversa. O Funil, que é "onde eu estou", fica.
   */
  useEffect(() => {
    if (novo === true || empresa !== undefined || pessoa !== undefined) {
      void navegar({
        search: (atual) => ({ ...atual, novo: undefined, empresa: undefined, pessoa: undefined }),
        replace: true,
      })
    }
  }, [novo, empresa, pessoa, navegar])
  /* A busca do cabeçalho (protótipo v2): filtra os cards do quadro aberto pelo nome da Empresa ou
     pelo código do Negócio. É estado da tela, e não da URL: é um recorte de leitura momentâneo, e
     não "onde eu estou". */
  const [busca, setBusca] = useState('')
  /* Estável: a janela depende dele nos efeitos, e uma função nova por render reataria os ouvintes
     — e o efeito do foco. */
  const fecharACriacao = useCallback(() => {
    setCriacao(null)
  }, [])

  const funis = catalogo.data ?? []
  const escolhido = funis.find((item) => item.id === funil) ?? funilPadrao(funis, sessao?.perfil)

  // **Empurra** uma entrada de histórico: trocar de Funil é virada de contexto. A pílula do
  // quadro e o seletor do formulário escrevem o mesmo parâmetro.
  const trocarDeFunil = (destino: string) => {
    void navegar({ search: (atual) => ({ ...atual, funil: destino }) })
  }

  // "Gerenciar funis e etapas" (11.2) só para escopo `todas` — conveniência: o serviço recusa.
  const gerencia = sessao !== undefined && ehDeGestao(sessao.escopo_de_carteira)

  return (
    <div className="tela-do-funil">
      {/* As ações do cabeçalho, no desenho do protótipo v2: a busca e, à direita, `+ Negócio` —
          a ação primária do Funil, e **só dele** (11.4, DESIGN.md). */}
      <AcoesDoCabecalho>
        <label className="tela-do-funil__busca">
          <span className="tela-do-funil__busca-lupa" aria-hidden="true">
            ⌕
          </span>
          <input
            type="search"
            className="tela-do-funil__busca-campo"
            placeholder={ROTULO_DA_BUSCA}
            aria-label={ROTULO_DA_BUSCA}
            value={busca}
            onChange={(evento) => {
              setBusca(evento.target.value)
            }}
          />
        </label>
        <button
          type="button"
          className="tela-do-funil__novo"
          onClick={() => {
            setCriacao({})
          }}
        >
          {ROTULO_DO_NOVO_NEGOCIO}
        </button>
      </AcoesDoCabecalho>
      {/* A recusa e a espera do catálogo descem ao quadro: sem Funil escolhido a leitura do
          quadro nem sai, e ele precisa saber distinguir "vindo" de "falhou" de "vazio". */}
      <QuadroDoFunil
        funis={funis}
        funil={escolhido}
        erroDoCatalogo={catalogo.isError ? catalogo.error : undefined}
        aoRecarregarOCatalogo={() => {
          void catalogo.refetch()
        }}
        catalogoCarregando={catalogo.isLoading}
        aoTrocarDeFunil={trocarDeFunil}
        aoCriarNegocio={() => {
          setCriacao({})
        }}
        busca={busca}
        acoesDaBarra={
          /* "Gerenciar funis e etapas" (11.2) mora na barra do quadro, ao lado das abas de Funil
             (protótipo v2), e só para escopo `todas` — conveniência: o serviço recusa. */
          gerencia ? (
            <Link className="tela-do-funil__gerenciar" to={CAMINHO_DE_GERENCIAR_FUNIS}>
              <span aria-hidden="true">⚙ </span>
              {TITULO_DE_GERENCIAR_FUNIS}
            </Link>
          ) : null
        }
      />
      {criacao === null ? null : (
        <JanelaDoNovoNegocio
          key={geracao}
          funilId={escolhido?.id}
          aoTrocarDeFunil={trocarDeFunil}
          aoFechar={fecharACriacao}
          chegada={criacao}
        />
      )}
    </div>
  )
}

export const rotaFunil = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHOS.funil,
  component: TelaDoFunil,
  /**
   * A URL é entrada de quem chega, e não só do que a tela escreveu: alguém edita a barra de
   * endereço, um link antigo circula, o histórico traz um valor de outra versão da tela. O que
   * não for texto cai fora **aqui**, e a tela recebe sempre um estado que ela sabe desenhar.
   *
   * O `uuid` **não** é conferido contra o catálogo aqui: a lista só existe depois da resposta do
   * serviço, e um validador que dependesse dela seria um validador que às vezes não valida. Quem
   * decide é a tela, comparando com os Funis que chegaram — e um `id` que não está lá cai no
   * padrão do perfil, em vez de num seletor sem valor.
   */
  validateSearch: (parametros: Record<string, unknown>): BuscaDoFunil => {
    const escolhido = typeof parametros.funil === 'string' ? parametros.funil.trim() : ''
    // O roteador lê `?novo=1` como o número 1 e `?novo=true` como booleano: os dois valem.
    const novo = ['1', 'true'].includes(String(parametros.novo))
    // Os ids da conversa (13.1): só `uuid`. O que não for cai fora aqui, e a janela abre sem ele.
    const uuid = (valor: unknown) =>
      typeof valor === 'string' && UUID.test(valor.trim()) ? valor.trim() : undefined
    const empresa = uuid(parametros.empresa)
    const pessoa = uuid(parametros.pessoa)
    /*
     * As chaves vão **sempre**, mesmo `undefined`: o roteador soma a busca validada à busca crua
     * do pai, e uma chave omitida aqui deixaria passar o valor cru — `empresa=nao-e-uuid` chegaria
     * à tela como se tivesse sido validado.
     */
    return {
      funil: escolhido === '' ? undefined : escolhido,
      ...(novo ? { novo: true } : {}),
      empresa,
      pessoa,
    }
  },
})
